import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import notificationService from '../../../services/notificationService';
import Icon from '../../../components/AppIcon';

const NotificationPreferences = () => {
  const { userProfile } = useAuth();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const prefs = await notificationService?.getNotificationPreferences();
      setPreferences(prefs);
    } catch (err) {
      console.error('Error fetching preferences:', err);
      setError(err?.message || 'Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');
      
      await notificationService?.updateNotificationPreferences(preferences);
      
      setSuccessMessage('Notification preferences saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError(err?.message || 'Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader" className="animate-spin text-accent" size={32} />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="Bell" size={24} className="text-accent" />
        <h2 className="text-2xl font-bold text-card-foreground">Notification Preferences</h2>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-error/10 border border-error rounded-md flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-success/10 border border-success rounded-md flex items-start gap-3">
          <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
          <p className="text-sm text-success">{successMessage}</p>
        </div>
      )}

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground mb-4 flex items-center gap-2">
            <Icon name="Mail" size={20} className="text-accent" />
            Email Notifications
          </h3>
          <div className="space-y-4 ml-7">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-md hover:bg-muted/50 transition-colors">
              <div>
                <p className="font-medium text-card-foreground">New Follower Alerts</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Receive an email immediately when someone follows you
                </p>
              </div>
              <button
                onClick={() => handleToggle('emailNewFollower')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
                  preferences?.emailNewFollower ? 'bg-accent' : 'bg-muted'
                }`}
                aria-label="Toggle new follower email notifications"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences?.emailNewFollower ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-md hover:bg-muted/50 transition-colors">
              <div>
                <p className="font-medium text-card-foreground">Daily Summary</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Get a daily digest of new followers and activity
                </p>
              </div>
              <button
                onClick={() => handleToggle('emailDailySummary')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
                  preferences?.emailDailySummary ? 'bg-accent' : 'bg-muted'
                }`}
                aria-label="Toggle daily summary email"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences?.emailDailySummary ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-md hover:bg-muted/50 transition-colors">
              <div>
                <p className="font-medium text-card-foreground">Weekly Summary</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Receive a weekly overview of your follower growth
                </p>
              </div>
              <button
                onClick={() => handleToggle('emailWeeklySummary')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
                  preferences?.emailWeeklySummary ? 'bg-accent' : 'bg-muted'
                }`}
                aria-label="Toggle weekly summary email"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences?.emailWeeklySummary ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-card-foreground mb-4 flex items-center gap-2">
            <Icon name="Smartphone" size={20} className="text-accent" />
            In-App Notifications
          </h3>
          <div className="space-y-4 ml-7">
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-md hover:bg-muted/50 transition-colors">
              <div>
                <p className="font-medium text-card-foreground">New Follower Alerts</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Show in-app notifications when someone follows you
                </p>
              </div>
              <button
                onClick={() => handleToggle('inAppNewFollower')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
                  preferences?.inAppNewFollower ? 'bg-accent' : 'bg-muted'
                }`}
                aria-label="Toggle in-app new follower notifications"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    preferences?.inAppNewFollower ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end gap-3">
        <button
          onClick={fetchPreferences}
          className="px-6 py-2 border border-border rounded-md text-card-foreground hover:bg-muted transition-colors"
          disabled={saving}
        >
          Reset
        </button>
        <button
          onClick={handleSave}
          className="px-6 py-2 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={saving}
        >
          {saving ? (
            <>
              <Icon name="Loader" size={16} className="animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Icon name="Save" size={16} />
              Save Preferences
            </>
          )}
        </button>
      </div>

      <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-md">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-card-foreground font-medium">About Notifications</p>
            <p className="text-sm text-muted-foreground mt-1">
              You can customize how you receive follower notifications. Email notifications are sent from our system, 
              while in-app notifications appear in your dashboard notification center.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationPreferences;